// src/context/CartContext.jsx (Revised for your setup)

import React, { createContext, useContext, useState, useEffect } from "react";
import api from "../api/api"; // Your Axios instance
import { useAuth } from "./AuthContext"; // ✅ IMPORT YOUR HOOK

const CartContext = createContext();

export const useCart = () => useContext(CartContext);

export const CartProvider = ({ children }) => {
  const { isLoggedIn } = useAuth();

  const [cartItems, setCartItems] = useState([]);
  const [loading, setLoading] = useState(false);

  // ✅ NEW STATE: Sidebar visibility
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);

  // ✅ NEW FUNCTION: Toggles the sidebar state
  const toggleSidebar = (forceOpen = null) => {
    setIsSidebarOpen((prev) => (forceOpen !== null ? forceOpen : !prev));
  };

  // 1. Fetch Cart on load/login
  const fetchCart = async () => {
    // Only proceed if the user is logged in
    if (!isLoggedIn) {
      setCartItems([]); // Clear cart if logged out
      return;
    }
    setLoading(true);
    try {
      // API request is implicitly authenticated by your Axios setup
      const response = await api.get("/user/cart");
      setCartItems(response.data);
    } catch (error) {
      console.error("Failed to fetch cart:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    // We only fetch if the user state changes AND they are logged in
    // This ensures fetching on initial load after successful auth session check
    if (isLoggedIn) {
      fetchCart();
    } else {
      setCartItems([]); // Clear cart state immediately on logout
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoggedIn]);

  // 2. Add/Update Item in Cart (uses existing logic)
  const addItemToCart = async (productId, quantity = 1) => {
    if (!isLoggedIn) return;

    try {
      await api.post("/user/cart", { product_id: productId, quantity });
      // After success, re-fetch to get the latest state
      await fetchCart();
      console.log("Product added/updated in cart.");
    } catch (error) {
      console.error("Failed to add to cart:", error);
      // You can add better error handling here (e.g., toast message)
    }
  };

  // 3. Update Quantity (specific item) - (uses existing logic)
  const updateItemQuantity = async (cartItemId, newQuantity) => {
    if (!isLoggedIn) return;

    if (newQuantity < 1) {
      await removeItemFromCart(cartItemId);
      return;
    }

    try {
      await api.put(`/user/cart/${cartItemId}`, { quantity: newQuantity });
      await fetchCart();
      console.log(
        `Cart item ${cartItemId} quantity updated to ${newQuantity}.`,
      );
    } catch (error) {
      console.error("Failed to update cart quantity:", error);
    }
  };

  // 4. Remove Item - (uses existing logic)
  const removeItemFromCart = async (cartItemId) => {
    if (!isLoggedIn) return;

    try {
      await api.delete(`/user/cart/${cartItemId}`);
      await fetchCart();
      console.log(`Cart item ${cartItemId} removed.`);
    } catch (error) {
      console.error("Failed to remove from cart:", error);
    }
  };

  const value = {
    cartItems,
    loading,
    addItemToCart,
    updateItemQuantity,
    removeItemFromCart,
    fetchCart,
    isSidebarOpen,
    toggleSidebar,
  };

  return <CartContext.Provider value={value}>{children}</CartContext.Provider>;
};
